import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Focus, Check, X, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JustTodayMode({ onExit, onPointsChange }) {
  const [tasks, setTasks] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/calendar/events?from_date=${new Date().toISOString()}&to_date=${new Date(Date.now() + 86400000 * 2).toISOString()}`, { withCredentials: true }),
      ]);
      const taskList = Array.isArray(t.data) ? t.data : (t.data.items || t.data.tasks || []);
      const openTasks = taskList.filter((x) => !x.is_completed).slice(0, 3);
      setTasks(openTasks);
      const events = (c.data.items || []).filter((e) => !e.completed && new Date(e.starts_at) >= new Date(Date.now() - 30 * 60000));
      setNextEvent(events[0] || null);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleTask = async (task) => {
    try {
      const endpoint = task.is_completed ? 'uncomplete' : 'complete';
      await axios.post(`${API}/tasks/${task.id}/${endpoint}`, {}, { withCredentials: true });
      if (!task.is_completed) toast.success('+1 ✨');
      onPointsChange?.();
      await fetchData();
    } catch { toast.error('Kunde inte uppdatera'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#FFE8F0] via-[#F3E8FF] to-[#E8F0FF] overflow-y-auto" data-testid="just-today-mode">
      <div className="max-w-lg mx-auto p-6 min-h-screen flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <Focus className="w-5 h-5" />
            <h1 className="font-bold font-['Fredoka'] text-lg">Idag räcker det</h1>
          </div>
          <button
            onClick={onExit}
            className="neo-btn px-3 py-1.5 bg-white text-sm flex items-center gap-1"
            data-testid="just-today-exit"
          >
            <X className="w-4 h-4" /> Avsluta
          </button>
        </div>

        <div className="flex-1 space-y-6">
          <div className="text-center mb-4">
            <p className="text-sm italic opacity-70">
              Andas ut. Allt annat kan vänta. Du behöver bara fokusera på det här 🌸
            </p>
          </div>

          <div className="neo-card bg-white p-5 border-[3px]">
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-3">✨ Dagens 3 prioriteringar</p>
            {tasks.length === 0 ? (
              <p className="text-sm opacity-60 italic py-4 text-center">
                Inga öppna tasks — det betyder att du redan gjort allt viktigt. 🎉<br/>
                Eller lägg till något litet i Tasks-fliken när du är redo.
              </p>
            ) : (
              <div className="space-y-2">
                {tasks.map((t) => (
                  <label
                    key={t.id}
                    className="flex items-start gap-3 p-3 rounded-xl border-2 border-black bg-[#FFFAF0] cursor-pointer hover:bg-white transition-colors"
                    data-testid={`just-today-task-${t.id}`}
                  >
                    <button
                      onClick={(e) => { e.preventDefault(); toggleTask(t); }}
                      className={`w-8 h-8 rounded-full border-2 border-black flex items-center justify-center flex-shrink-0 ${t.is_completed ? 'bg-[#B9FBC0]' : 'bg-white'}`}
                    >
                      {t.is_completed && <Check className="w-4 h-4" />}
                    </button>
                    <span className={`text-base font-semibold ${t.is_completed ? 'line-through opacity-50' : ''}`}>
                      {t.title}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {nextEvent && (
            <div className="neo-card bg-[#FFE8F0] p-4 border-[3px]" data-testid="just-today-next-event">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1">🔔 Nästa viktiga</p>
              <p className="font-bold font-['Fredoka'] text-base">{nextEvent.title}</p>
              <p className="text-sm opacity-75 flex items-center gap-1 mt-1">
                <CalendarClock className="w-4 h-4" />
                {new Date(nextEvent.starts_at).toLocaleString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          )}

          <div className="text-center mt-auto pt-6">
            <p className="text-xs opacity-50 italic">
              Mer kan du göra imorgon. Idag räcker det 💛
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
